import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useCountry } from "../context/CountryContext";

// ===== CONSTANTS =====
const NOMBA_PAYMENT_LINK ="https://flutterwave.com/pay/aotiy7qqfatz";
const SUPPORT_EMAIL = "probanksupport@gmail.com";
const SUPPORT_WHATSAPP = "27652611261";
const API_BASE = "https://alkebulanfkalertbackend.onrender.com/api";

// ===== HELPER: always reads fresh from localStorage =====
const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch { return {}; }
};

const SubscriptionPaymentPage = () => {
  const { isGhana } = useCountry();

  const [step, setStep] = useState("details");
  const [toast, setToast] = useState("");
  const [userData, setUserData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [countdown, setCountdown] = useState(5);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const plan = params.get("plan") || "basic";
  const userId = params.get("userId");
  const amount = Number(params.get("amount")) || 0;
  const urlCountry = params.get("country");

  const useGhana = urlCountry ? urlCountry === "GH" : isGhana;

  const theme = useGhana
    ? {
        bgGradient: "from-[#1a0500] via-black to-[#0a0500]",
        amountColor: "text-yellow-400",
        primaryBtn: "from-yellow-500 to-red-600",
        currencySymbol: "₵",
        currency: "GHS",
        flag: "🇬🇭",
        countryName: "Ghana",
        bankName: "Telecel Cash",
        accountNumber: "0507557845",
        accountName: "LUCY ASARE",
        bankLabel: "Provider",
        accountLabel: "Mobile Money Number",
        isNomba: false,
      }
    : {
        bgGradient: "from-black via-gray-900 to-black",
        amountColor: "text-green-400",
        primaryBtn: "from-green-500 to-emerald-600",
        currencySymbol: "₦",
        currency: "NGN",
        flag: "🇳🇬",
        countryName: "Nigeria",
        bankName: "Nomba Secure Checkout",
        accountNumber: "",
        accountName: "",
        bankLabel: "Payment Via",
        accountLabel: "Account Number",
        isNomba: true,
      };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  // ✅ Fetch user — first try localStorage (instant), then backend (fresh)
  useEffect(() => {
    const loadUser = async () => {
      const stored = getStoredUser();
      if (stored.name || stored.email) {
        setUserData(stored);
      }
      try {
        const email = stored?.email;
        if (!email) { setLoadingUser(false); return; }
        const res = await axios.get(`${API_BASE}/users`, { params: { email } });
        if (res.data) setUserData(res.data);
      } catch (err) {
        console.error("User fetch error:", err.message);
      } finally {
        setLoadingUser(false);
      }
    };
    loadUser();
  }, []);

  // Countdown for Nomba redirect
  useEffect(() => {
    if (step !== "nomba") return;
    setCountdown(5);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [step]);

  const copyAccount = () => {
    navigator.clipboard.writeText(theme.accountNumber);
    showToast("✅ Number copied");
  };

  const copyAmount = () => {
    navigator.clipboard.writeText(String(amount));
    showToast(`✅ ${theme.currencySymbol}${amount.toLocaleString()} copied!`);
  };

  // ✅ Always build messages with fresh data at call time
  const buildMessages = () => {
    const stored = getStoredUser();
    const name = userData?.name || stored.name || stored.fullName || "Not provided";
    const email = userData?.email || stored.email || "Not provided";
    const uid = userId || stored._id || stored.id || "Not provided";

    const proofBody = `Hello Support,\n\nI have completed my subscription payment.\n\nName: ${name}\nEmail: ${email}\nUser ID: ${uid}\nPlan: ${plan.toUpperCase()}\nAmount: ${theme.currencySymbol}${amount.toLocaleString()}\nCountry: ${theme.flag} ${theme.countryName}\n\nPlease activate my subscription.\n\nThank you.`;

    const helpBody = `Hello Support,\n\nI am having trouble with my ${plan.toUpperCase()} subscription payment.\n\nName: ${name}\nEmail: ${email}\nUser ID: ${uid}\nAmount: ${theme.currencySymbol}${amount.toLocaleString()}\n\nPlease assist me.\n\nThank you.`;

    const waProof = encodeURIComponent(`Hello ProBank Support, I have completed my ${plan.toUpperCase()} plan payment.\n\nName: ${name}\nEmail: ${email}\nPlan: ${plan.toUpperCase()}\nAmount: ${theme.currencySymbol}${amount.toLocaleString()}\n\nPlease activate my subscription.`);

    const waHelp = encodeURIComponent(`Hello ProBank Support, I am having trouble with my ${plan.toUpperCase()} subscription payment.\n\nName: ${name}\nEmail: ${email}\nAmount: ${theme.currencySymbol}${amount.toLocaleString()}\n\nPlease help me.`);

    return {
      emailProof: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Subscription Proof - ${plan.toUpperCase()} - ${name}`)}&body=${encodeURIComponent(proofBody)}`,
      emailHelp: `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Subscription Payment Problem - ${name}`)}&body=${encodeURIComponent(helpBody)}`,
      waProof: `https://wa.me/${SUPPORT_WHATSAPP}?text=${waProof}`,
      waHelp: `https://wa.me/${SUPPORT_WHATSAPP}?text=${waHelp}`,
    };
  };

  const OPayWarning = () => (
    <div className="p-4 rounded-2xl bg-red-500/15 border-2 border-red-500/60">
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0">🚫</span>
        <div>
          <p className="font-bold text-red-300 text-sm mb-1">DO NOT PAY FROM OPAY</p>
          <p className="text-red-200 text-xs leading-relaxed">
            We do <span className="font-bold underline">NOT</span> accept OPay payments.
            Use GTBank, Access, Zenith, UBA, Kuda, Moniepoint, Palmpay or any other bank.
          </p>
        </div>
      </div>
    </div>
  );

  const SupportButtons = ({ proof = false }) => {
    const msgs = buildMessages();
    return (
      <div className="space-y-2">
        <p className="text-xs text-gray-400 text-center">
          {proof ? "Send your payment proof:" : "Having payment trouble?"}
        </p>
        <a href={proof ? msgs.waProof : msgs.waHelp} target="_blank" rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-green-600 hover:bg-green-700 transition font-semibold text-white text-sm shadow-lg">
          💬 {proof ? "Send Proof on WhatsApp (Fastest)" : "WhatsApp Support (Fastest)"}
        </a>
        <a href={proof ? msgs.emailProof : msgs.emailHelp}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 transition font-semibold text-white text-sm shadow-lg">
          📧 {proof ? "Send Proof by Email" : "Email Support"}
        </a>
      </div>
    );
  };

  const displayName = userData?.name || getStoredUser().name || "";

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${theme.bgGradient} text-white px-4 py-8`}>
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{theme.countryName}</span>
      </div>

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 bg-black/80 border border-white/10 px-4 py-2 rounded-xl shadow-lg backdrop-blur z-50 text-sm">
          {toast}
        </div>
      )}

      <div className="w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-2xl">
        <h2 className="text-xl font-bold text-center mb-1 tracking-wide">
          {plan.toUpperCase()} PLAN PAYMENT
        </h2>
        <p className="text-center text-xs text-gray-400 mb-5">
          {loadingUser ? "Loading your details..." : displayName ? `For: ${displayName}` : `${theme.flag} ${theme.countryName}`}
        </p>

        {/* STEP: DETAILS */}
        {step === "details" && (
          <div className="space-y-4">
            <OPayWarning />

            <div className="p-4 rounded-xl border border-white/10 bg-white/5 text-center">
              <p className="text-xs text-gray-400 mb-1">Amount to Pay</p>
              <p className={`${theme.amountColor} font-bold text-3xl`}>
                {theme.currencySymbol}{amount.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">{plan.toUpperCase()} plan · {theme.currency}</p>
            </div>

            {!theme.isNomba && (
              <>
                <div className="p-4 rounded-xl border border-white/10 bg-white/5">
                  <p className="text-xs text-gray-400">{theme.bankLabel}</p>
                  <p className="text-sm font-medium">{theme.bankName}</p>
                </div>
                <div className="p-4 rounded-xl border border-white/10 bg-white/5 flex justify-between items-center">
                  <div>
                    <p className="text-xs text-gray-400">{theme.accountLabel}</p>
                    <p className="text-sm font-medium">{theme.accountNumber}</p>
                  </div>
                  <button onClick={copyAccount} className="text-xs px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 transition">Copy</button>
                </div>
                <div className="p-4 rounded-xl border border-white/10 bg-white/5">
                  <p className="text-xs text-gray-400">Account Name</p>
                  <p className="text-sm font-medium">{theme.accountName}</p>
                </div>
              </>
            )}

            {theme.isNomba && (
              <div className="p-4 rounded-xl border border-green-400/30 bg-green-500/10 text-center">
                <p className="text-xs text-gray-400 mb-1">Payment via</p>
                <p className="text-green-400 font-semibold">Nomba Secure Checkout</p>
                <p className="text-xs text-gray-500 mt-1">Card • Bank Transfer • USSD</p>
              </div>
            )}

            <button
              onClick={() => theme.isNomba ? setStep("nomba") : setStep("confirm")}
              className={`w-full py-3 rounded-xl bg-gradient-to-r ${theme.primaryBtn} hover:opacity-90 transition font-bold text-white shadow-lg`}
            >
              {theme.isNomba ? `Pay ${theme.currencySymbol}${amount.toLocaleString()}` : "I Have Paid"}
            </button>

            <SupportButtons proof={false} />
          </div>
        )}

        {/* STEP: NOMBA INSTRUCTIONS */}
        {step === "nomba" && (
          <div className="space-y-4">
            <OPayWarning />

            <div className="p-5 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-2 border-green-400/40 space-y-3">
              <p className="text-center text-green-300 font-semibold text-sm">Step 1: Copy the amount</p>
              <button onClick={copyAmount}
                className="w-full p-4 rounded-xl bg-black/40 border border-green-400/30 hover:bg-black/60 transition flex items-center justify-between">
                <span className="text-2xl font-bold text-green-400">{theme.currencySymbol}{amount.toLocaleString()}</span>
                <span className="text-xs bg-green-500 text-black font-bold px-3 py-2 rounded-lg">📋 TAP TO COPY</span>
              </button>
            </div>

            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-400/30 space-y-2">
              <p className="text-blue-300 font-semibold text-sm">📲 On the payment page:</p>
              <ol className="text-blue-100 text-sm space-y-1.5 pl-1">
                <li><b className="text-blue-400">2.</b> Paste <b className="text-green-300">{amount.toLocaleString()}</b> in the amount field</li>
                <li><b className="text-blue-400">3.</b> Choose your payment method</li>
                <li><b className="text-blue-400">4.</b> Complete payment</li>
                <li><b className="text-blue-400">5.</b> Come back and send proof</li>
              </ol>
            </div>

            <button
              onClick={() => window.location.href = NOMBA_PAYMENT_LINK}
              disabled={countdown > 0}
              className={`w-full py-4 rounded-xl font-bold text-base shadow-lg transition-all ${
                countdown > 0
                  ? "bg-gray-600 cursor-not-allowed text-gray-300"
                  : "bg-gradient-to-r from-green-500 to-emerald-600 hover:brightness-110 text-white"
              }`}
            >
              {countdown > 0 ? `Please read first... (${countdown}s)` : "✅ Take Me to Payment"}
            </button>

            <button onClick={() => setStep("confirm")} className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm transition">
              I Already Paid — Submit Proof
            </button>
            <button onClick={() => setStep("details")} className="w-full py-1 text-xs text-gray-400 hover:text-white">← Go back</button>
          </div>
        )}

        {/* STEP: CONFIRM */}
        {step === "confirm" && (
          <div className="text-center space-y-4">
            <div className="text-5xl">✅</div>
            <h3 className="text-xl font-bold">Payment Submitted!</h3>
            <p className="text-gray-400 text-sm">
              Your <span className="text-white font-semibold">{plan.toUpperCase()}</span> subscription
              will be activated once your payment is confirmed.
            </p>
            <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-400/30 text-yellow-300 text-sm">
              ⏳ Activation usually takes a few hours. You will be notified.
            </div>

            <SupportButtons proof={true} />

            <div className="pt-2 space-y-2">
              <p className="text-xs text-gray-400">Having a payment problem?</p>
              <a href={buildMessages().waHelp} target="_blank" rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition text-white text-sm">
                💬 Report Payment Problem
              </a>
              <a href={buildMessages().emailHelp}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition text-white text-sm">
                📧 Report by Email
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPaymentPage;
